import type { VirtualBudtenderQuery } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { LINEAGE_COLORS, isEffectsDisabledState } from '@iheartjane/menu';
import type { Nullable } from '@iheartjane/types';
import isEmpty from 'lodash-es/isEmpty';
import max from 'lodash-es/max';
import min from 'lodash-es/min';
import omit from 'lodash-es/omit';
import { useMemo } from 'react';

import type {
  AlgoliaFacets,
  AppOptions,
  FilterGroup,
  FilterKeys,
  MenuOptions,
  RangeFilterGroup,
  SelectFilterGroup,
} from '@jane/search/types';
import {
  ACTIVITY_ICONS,
  FEELING_ICONS,
  JANE_GOLD_ICON,
  LINEAGE_ICONS,
  buildCategoryFilterItems,
  buildSaleFilterItems,
  facetToFilter,
  getPriceBucketCount,
  getRatingBucketCount,
  sortAndLabelSpecials,
  sortFilterItems,
  sortWeights,
} from '@jane/search/util';
import { useShouldShowGold } from '@jane/shared/hooks';

interface FormattedProductFilterProps {
  appOptions?: AppOptions;
  facets: AlgoliaFacets;
  menuOptions?: MenuOptions;
  virtualBudtenderQueries?: VirtualBudtenderQuery[];
}

/**
 * Combines the facets for bundle qualify and discount into one object, summing counts where keys (special IDs) overlap.
 * This is necessary because Algolia returns these facets separately, but for UI purposes they should be treated as one.
 * @param params.qualifyFacets - Facets for bundle qualify.
 * @param params.discountFacets - Facets for bundle discount.
 * @returns Combined facets with summed counts.
 */
export const getApplicableBundleSpecialFacets = ({
  qualifyFacets,
  discountFacets,
}: {
  discountFacets: Record<string, number>;
  qualifyFacets: Record<string, number>;
}) => {
  const combined: Record<string, number> = { ...discountFacets };
  Object.entries(qualifyFacets).forEach(([key, value]) => {
    if (combined[key]) {
      combined[key] += value;
    } else {
      combined[key] = value;
    }
  });
  return combined;
};

/**
 * Formats Algolia facets into filters ready for ui:
 * @param {AlgoliaFacets} [facets] - All product facets from Algolia.
 * @param {AppOptions} [appOptions] - App options that determine what renders where
 * @param {MenuOptions} [menuOptions] - Menu options encompass all customization options for store
 * @param {VirtualBudtenderQuery[]} [virtualBudtenderQueries] - Optional virtual budtender queries.
 */

export const useFormattedProductFilters = ({
  facets,
  appOptions,
  menuOptions,
  virtualBudtenderQueries,
}: FormattedProductFilterProps): FilterGroup[] => {
  const hasVirtualBudtenderEnabled = useFlag(FLAGS.dmVirtualBudtenderEnabled);

  const {
    customLabels,
    customRanking,
    customRows,
    disabledFilters,
    hasBrandSpecials = true,
    menuTabs,
    showOnlyWeights = [],
    showRatings,
    specials,
    specialsLabel,
    storeId,
    storeState,
    userLocationState,
  } = menuOptions || {};

  const { appMode, brandPage, brandSpecialPage, categoryPage, specialsPage } =
    appOptions || {};

  const showJaneGold = useShouldShowGold({
    ...(storeId && { store: { id: storeId, state: storeState } }),
  });

  return useMemo(() => {
    const branded = appMode === 'default';

    const filtersToOmit = disabledFilters || [];
    const hideEffects =
      isEffectsDisabledState(storeState) ||
      isEffectsDisabledState(userLocationState);

    const categoryLabel = categoryPage ? 'Subcategory' : 'Category';

    const categoryFilters: Nullable<SelectFilterGroup> = facets.root_types
      ? {
          items: buildCategoryFilterItems({
            branded,
            categoryPage: Boolean(categoryPage),
            customLabels: customLabels?.root_types,
            customRanking,
            menuTabs,
            rootTypeFacets: facets.root_types,
            storeId,
            storeState,
          }),
          key: 'root_types',
          label: categoryLabel,
          nested: true,
          showInFilterBar: true,
          type: 'multiselect',
        }
      : null;

    const brandFilters: Nullable<SelectFilterGroup> =
      !brandPage && !brandSpecialPage && facets.brand
        ? {
            items: sortFilterItems({
              items: facetToFilter(facets.brand, {
                filterGroup: 'Brand',
              }),
            }),
            key: 'brand',
            label: 'Brand',
            showInFilterBar: true,
            type: 'multiselect',
            typeahead: true,
          }
        : null;

    const activityFilters: Nullable<SelectFilterGroup> =
      !hideEffects && facets.activities
        ? {
            items: facetToFilter(omit(facets.activities, filtersToOmit), {
              icons: ACTIVITY_ICONS,
            }),
            key: 'activities',
            label: 'Activity',
            type: 'multiselect',
            showInFilterBar: false,
          }
        : null;

    const feelingFilters: Nullable<SelectFilterGroup> =
      !hideEffects && facets.feelings
        ? {
            items: facetToFilter(omit(facets.feelings, filtersToOmit), {
              icons: FEELING_ICONS,
            }),
            key: 'feelings',
            label: 'Feeling',
            showInFilterBar: !storeId,
            type: 'multiselect',
          }
        : null;

    const lineageFilters: Nullable<SelectFilterGroup> = facets.category
      ? {
          items: facetToFilter(facets.category, {
            customLabels: customLabels?.category,
            iconColor: LINEAGE_COLORS,
            icons: LINEAGE_ICONS,
            storeId,
          }),
          key: 'category',
          label: 'Lineage',
          showInFilterBar: true,
          type: 'multiselect',
        }
      : null;

    const hasSpecialsLabel = specialsLabel && specialsLabel?.trim().length > 0;

    const specialsFilters: Nullable<SelectFilterGroup> =
      !specialsPage &&
      specials &&
      (facets.applicable_special_ids ||
        facets.applicable_cart_total_special_ids ||
        facets['applicable_bundle_special_ids.discount'] ||
        facets['applicable_bundle_special_ids.qualify'] ||
        facets.applicable_bulk_special_ids)
        ? {
            key: 'applicable_special_ids',
            label: hasSpecialsLabel ? specialsLabel : 'Deal',
            showInFilterBar: true,
            type: 'multiselect',
            items: sortAndLabelSpecials(specials)(
              {
                ...facets.applicable_bulk_special_ids,
                ...getApplicableBundleSpecialFacets({
                  qualifyFacets:
                    facets['applicable_bundle_special_ids.qualify'] ?? {},
                  discountFacets:
                    facets['applicable_bundle_special_ids.discount'] ?? {},
                }),
                ...facets.applicable_cart_total_special_ids,
                ...facets.applicable_special_ids,
              },
              appMode
            ),
          }
        : null;

    const ratingFilters: Nullable<SelectFilterGroup> =
      showRatings && facets.aggregate_rating
        ? {
            key: 'aggregate_rating',
            label: 'Rating',
            showInFilterBar: false,
            modalItemsPerRow: 4,
            type: 'multiselect',
            items: getRatingBucketCount(facets.aggregate_rating),
          }
        : null;

    const terpeneFilters: Nullable<SelectFilterGroup> = facets.compound_names
      ? {
          items: sortFilterItems({
            items: facetToFilter(facets.compound_names, {
              filterGroup: 'Terpenes',
            }),
          }),
          key: 'compound_names',
          label: 'Top terpenes',
          showInFilterBar: false,
          type: 'multiselect',
          typeahead: true,
        }
      : null;

    const thcPercentages = Object.keys(facets.percent_thc ?? {}).map(
      (percent) => parseFloat(percent)
    );
    const thcaPercentages = Object.keys(facets.percent_thca ?? {}).map(
      (percent) => parseFloat(percent)
    );
    const cbdPercentages = Object.keys(facets.percent_cbd ?? {}).map(
      (percent) => parseFloat(percent)
    );

    const potencyFilters: Nullable<RangeFilterGroup> = storeId
      ? {
          items: [
            {
              key: 'percent_thc',
              label: 'THC',
              max: max(thcPercentages) ?? 0,
              min: min(thcPercentages) ?? 0,
            },
            {
              key: 'percent_thca',
              label: 'THCa',
              max: max(thcaPercentages) ?? 0,
              min: min(thcaPercentages) ?? 0,
            },
            {
              key: 'percent_cbd',
              label: 'CBD',
              max: max(cbdPercentages) ?? 0,
              min: min(cbdPercentages) ?? 0,
            },
          ],
          key: 'percent_thc',
          label: '',
          nested: true,
          showInFilterBar: false,
          type: 'range',
        }
      : null;

    const showOnlyWeightFacets =
      !!showOnlyWeights?.length &&
      showOnlyWeights.reduce((prev, curr) => {
        const weight = curr.replace('_', ' ');
        const facet = facets.available_weights?.[weight];
        return facet ? { ...prev, [weight]: facet } : prev;
      }, {});

    const productWeights = showOnlyWeightFacets
      ? sortWeights(showOnlyWeightFacets)
      : facets.available_weights && sortWeights(facets.available_weights);

    const weightFilters: Nullable<SelectFilterGroup> =
      storeId && productWeights
        ? {
            items: facetToFilter(productWeights, {
              filterGroup: 'Weight',
            }),
            key: 'available_weights',
            label: 'Weight',
            showInFilterBar: false,
            modalItemsPerRow: 6,
            type: 'multiselect',
          }
        : null;

    const priceFilters: Nullable<SelectFilterGroup> = facets.bucket_price
      ? {
          key: 'bucket_price',
          label: 'Price',
          showInFilterBar: false,
          modalItemsPerRow: 5,
          type: 'multiselect',
          items: getPriceBucketCount(facets.bucket_price),
        }
      : null;

    const goldCounts = omit(facets.has_brand_discount, 'false');

    const janeGoldFilter: Nullable<SelectFilterGroup> =
      showJaneGold &&
      hasBrandSpecials &&
      !brandSpecialPage &&
      !isEmpty(goldCounts)
        ? {
            key: 'has_brand_discount',
            label: 'Jane Gold',
            showInFilterBar: true,
            showInFilterModal: false,
            modalItemsPerRow: 2,
            type: 'singleselect',
            items: facetToFilter(goldCounts, {
              filterGroup: 'Jane Gold',
              customLabels: { true: 'Jane Gold' },
              icons: JANE_GOLD_ICON,
            }),
          }
        : null;

    const renderJaneGoldFilter =
      showJaneGold && !brandSpecialPage && !isEmpty(goldCounts);

    const saleFilterItems = buildSaleFilterItems({
      branded,
      customLabels: customLabels?.root_types,
      customRows,
      rootTypeFacets: facets.root_types,
      storeId,
      storeState,
    });

    const dealsAndRewardsFilters: Nullable<SelectFilterGroup> =
      renderJaneGoldFilter || saleFilterItems.length > 0
        ? {
            key: 'deals_and_rewards' as FilterKeys, // casting since this is a custom key that is not actually used for filtering
            label: 'Deals and rewards',
            showInFilterBar: false,
            modalItemsPerRow: 2,
            type: 'multiselect',
            items: [
              ...(renderJaneGoldFilter
                ? facetToFilter(goldCounts, {
                    filterGroup: 'Jane Gold',
                    customLabels: { true: 'Jane Gold' },
                    icons: JANE_GOLD_ICON,
                    key: 'has_brand_discount',
                  })
                : []),
              ...saleFilterItems,
            ],
          }
        : null;

    const virtualBudtenderFilters: Nullable<SelectFilterGroup> =
      hasVirtualBudtenderEnabled && virtualBudtenderQueries?.length
        ? {
            key: 'virtual_budtender_queries',
            label: 'Explore',
            showInFilterBar: true,
            type: 'clickableOptions',
            showInFilterModal: false,
            items: virtualBudtenderQueries.map((query) => ({
              key: 'virtual_budtender_queries',
              label: query.displayText,
              count: 1,
              value: `${query.displayText}:${query.query}`,
            })),
          }
        : null;

    return [
      dealsAndRewardsFilters,
      janeGoldFilter,
      virtualBudtenderFilters,
      categoryFilters,
      brandFilters,
      lineageFilters,
      specialsFilters,
      feelingFilters,
      activityFilters,
      ratingFilters,
      potencyFilters,
      terpeneFilters,
      priceFilters,
      weightFilters,
    ].filter<FilterGroup>(
      (filterGroup): filterGroup is FilterGroup => filterGroup !== null
    );
  }, [facets, menuOptions, appOptions]);
};
